// Window Helper Utilities
// This file provides centralized helper functions for window operations

import { windowStylesByType, WindowStyleConfig } from '../config/windowStyles';
import { getImagePathForStyle } from './windowImages';

// Define the base path for window images
const BASE_IMAGE_PATH = 'images/windows';

// Default image to use if no match is found
export const DEFAULT_WINDOW_IMAGE = `${BASE_IMAGE_PATH}/Casements/Traditional/Traditional Style One.jpg`;

/**
 * Tilt-and-turn window paths with special handling, since they often have custom images
 */
export const tiltAndTurnImagePaths: Record<number, string> = {
  1: '/images/windows/TiltAndTurn/Button Operation.jpg',
  2: '/images/windows/TiltAndTurn/Handle Operation.jpg', 
  3: '/images/windows/TiltAndTurn/Left Side-Hung.jpg',
  4: '/images/windows/TiltAndTurn/Right-Side Hung.jpg',
  5: '/images/windows/TiltAndTurn/Double Casement.jpg',
  6: '/images/windows/TiltAndTurn/Middle Bottom Opener.jpg',
  7: '/images/windows/TiltAndTurn/Traditional Style Bottom Openers.jpg',
  52: '/images/windows/TiltAndTurn/Button Opener Fixed Fan.jpg',
  54: '/images/windows/TiltAndTurn/Double Casement.jpg',
  59: '/images/windows/TiltAndTurn/Double Casement.jpg'
};

/**
 * Special mapping for casement window styles
 */
export const casementImagePaths: Record<number, string> = {
  1: '/images/windows/Casements/Traditional/Traditional Style One.jpg',
  2: '/images/windows/Casements/Traditional/Traditional Style Two.jpg',
  3: '/images/windows/Casements/Traditional/Traditional Style Three.jpg',
  4: '/images/windows/Casements/Traditional/Traditional Style Four.jpg',
  5: '/images/windows/Casements/Traditional/Traditional Style Five.jpg'
};

/**
 * Gets the appropriate image path for a window based on its type and style ID
 * This is a centralized function that handles all the special cases
 */
export function getWindowImagePath(windowType: string, windowStyleId: number | string, storedImagePath?: string): string {
  // First preference: stored image path if available
  if (storedImagePath) {
    return storedImagePath;
  }
  
  const styleId = typeof windowStyleId === 'string' ? parseInt(windowStyleId) : windowStyleId;
  const type = windowType?.toLowerCase() || '';
  
  // Second preference: special handling for tilt-and-turn windows
  if (type.includes('tilt') && type.includes('turn')) {
    if (tiltAndTurnImagePaths[styleId]) {
      return tiltAndTurnImagePaths[styleId];
    }
    // Default tilt-and-turn if we don't have a specific mapping
    return '/images/windows/TiltAndTurn/Double Casement.jpg';
  }
  
  // Third preference: special handling for casement windows
  if (type.includes('casement')) {
    if (casementImagePaths[styleId]) {
      return casementImagePaths[styleId];
    }
  }
  
  // Fourth preference: find style in window styles config
  const styles = windowStylesByType[windowType] || [];
  const style = styles.find((s: WindowStyleConfig) => s.id === styleId);
  if (style && style.previewPath) {
    return style.previewPath;
  }
  
  // Final fallback: use utility function  
  const styleName = `Style ${styleId}`;
  return getImagePathForStyle(windowType, styleName);
}

/**
 * Gets a window type name from its ID
 */
export function getWindowTypeName(typeId: string): string {
  switch (typeId) {
    case 'casement':
      return 'Casement';
    case 'tilt-and-turn':
      return 'Tilt and Turn';
    case 'flush-casement':
      return 'Flush Casement';
    case 'sliding-sash':
      return 'Sliding Sash';
    case 'aluminium':
      return 'Aluminium';
    default:
      return typeId.charAt(0).toUpperCase() + typeId.slice(1);
  }
}

/**
 * Validates a window image path
 * @returns true if the image is valid/exists, false otherwise
 */
export function validateWindowImage(imagePath: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (!imagePath) {
      resolve(false);
      return;
    }
    
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.crossOrigin = 'anonymous';

    // Make sure we have a proper URL
    if (imagePath.startsWith('data:') || imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      img.src = imagePath;
    } else {
      // Handle relative paths
      img.src = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    }
  });
}

/**
 * Validates all window images in a set of items
 * @returns Array of item IDs with invalid images
 */
export async function validateWindowImages(items: any[]): Promise<string[]> {
  const failedImages: string[] = [];
  
  for (const item of items) {
    if (!item.imagePath) {
      failedImages.push(item.id);
      continue;
    }
    
    try {
      const isValid = await validateWindowImage(item.imagePath);
      if (!isValid) {
        failedImages.push(item.id);
      }
    } catch (error) {
      console.error(`Image validation failed for ${item.id}:`, error);
      failedImages.push(item.id);
    }
  }
  
  return failedImages;
} 